import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import CoutureLayout from "./CoutureLayout";
import ImageUpload from "../../components/admin/ImageUpload";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export default function CoutureSettings() {
    const { t } = useI18n();
    const [draft, setDraft] = useState({ name_fr: "", name_en: "", sub_fr: "", sub_en: "", logo_url: "", phone: "", address: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/couture/settings").then((r) => setDraft((d) => ({ ...d, ...r.data }))).catch(() => {});
    }, []);

    const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
    const setInput = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                name_fr: draft.name_fr,
                name_en: draft.name_en,
                sub_fr: draft.sub_fr,
                sub_en: draft.sub_en,
                logo_url: draft.logo_url,
                phone: draft.phone,
                address: draft.address,
            };
            const { data } = await api.put("/couture/settings", payload);
            setDraft((d) => ({ ...d, ...data }));
            toast.success(t("couture.settings_saved"));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-gold transition";
    const labelCls = "text-[0.65rem] tracking-[0.22em] uppercase font-bold text-gray-500";

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10 max-w-3xl" data-testid="couture-settings-page">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl text-brand-navy">{t("couture.settings")}</h1>
                        <div className="w-12 h-px bg-brand-gold mt-2" />
                    </div>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="couture-settings-save"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <Save className="w-4 h-4" /> {saving ? "…" : t("settings.save_all")}
                    </button>
                </div>

                <div className="bg-white border border-gray-100 p-6 space-y-8">
                    <p className="text-xs text-gray-400">{t("couture.settings_hint")}</p>

                    <div>
                        <div className={labelCls + " mb-2"}>{t("settings.identity_logo")}</div>
                        <ImageUpload value={draft.logo_url || ""} onChange={set("logo_url")} testid="couture-settings-logo" label={t("admin.upload_photo")} />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                        <label className="block">
                            <span className={labelCls}>{t("settings.identity_name")} · {t("settings.fr")}</span>
                            <input value={draft.name_fr || ""} onChange={setInput("name_fr")} data-testid="couture-settings-name-fr" className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("settings.identity_name")} · {t("settings.en")}</span>
                            <input value={draft.name_en || ""} onChange={setInput("name_en")} data-testid="couture-settings-name-en" className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("settings.identity_sub")} · {t("settings.fr")}</span>
                            <input value={draft.sub_fr || ""} onChange={setInput("sub_fr")} data-testid="couture-settings-sub-fr" className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("settings.identity_sub")} · {t("settings.en")}</span>
                            <input value={draft.sub_en || ""} onChange={setInput("sub_en")} data-testid="couture-settings-sub-en" className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("settings.phone_wa")}</span>
                            <input value={draft.phone || ""} onChange={setInput("phone")} data-testid="couture-settings-phone" className={inputCls} />
                        </label>
                        <label className="block">
                            <span className={labelCls}>{t("settings.address")}</span>
                            <input value={draft.address || ""} onChange={setInput("address")} data-testid="couture-settings-address" className={inputCls} />
                        </label>
                    </div>
                </div>
            </div>
        </CoutureLayout>
    );
}
